import { environment } from '../environments/environment';

// Single, flag-gated entry point for Amplitude browser analytics.
// No-op unless environment.amplitude.enabled is explicitly true, so no data leaves
// the app until it is enabled per-environment after privacy/security sign-off.
// The SDK is lazy-loaded via dynamic import so it stays out of the initial bundle
// while the flag is off. Session Replay is intentionally disabled (sampleRate 0):
// this app handles sensitive underwriting PII and recording sessions would require
// separate legal/privacy review.
type AmplitudeModule = typeof import('@amplitude/unified');

// Stamped onto every event (autocapture + custom) by the enrichment plugin below,
// mirroring LoggingService's App Insights initializer. Null until the app knows them.
interface AmplitudeContext {
    TenantKey: string | null;
    AreaKey: string | null;
    AccountID: string | null;
    WorkflowID: string | null;
}

const eventContext: AmplitudeContext = {
    TenantKey: null,
    AreaKey: null,
    AccountID: null,
    WorkflowID: null,
};

// Callers populate these as they become known; values persist until changed.
export function setAmplitudeContext(partial: Partial<AmplitudeContext>): void {
    Object.assign(eventContext, partial);
}

let amplitudePromise: Promise<AmplitudeModule> | undefined;
let homePageTracked = false;

// Lazy-loads and initializes the SDK once, or returns undefined when the flag is off.
function loadAmplitude(): Promise<AmplitudeModule> | undefined {
    const config = environment.amplitude;
    if (!config?.enabled) {
        return undefined;
    }
    if (!config.apiKey) {
        console.warn('Amplitude API key missing — analytics disabled');
        return undefined;
    }

    if (!amplitudePromise) {
        amplitudePromise = import('@amplitude/unified').then(async (amplitude) => {
            await amplitude.initAll(config.apiKey, {
                analytics: { autocapture: true },
                sessionReplay: { sampleRate: 0 },
            });
            amplitude.add({
                name: 'suw-context-enrichment',
                type: 'enrichment',
                execute: async (event: any) => {
                    event.event_properties = {
                        ...eventContext,
                        ...event.event_properties,
                        Environment: environment.env,
                    };
                    return event;
                },
            });
            return amplitude;
        });
    }

    return amplitudePromise;
}

export async function initAmplitude(): Promise<void> {
    try {
        const amplitude = await loadAmplitude();
        if (!amplitude || homePageTracked) {
            return;
        }

        homePageTracked = true;

        // helps improve this setup flow — safe to remove once you've verified the event lands
        amplitude.track('Viewed Home Page', { prompt_version: 'BA400.4' });
    } catch {
        // analytics is best-effort; never let it disrupt app startup
    }
}

export async function trackEvent(eventName: string, eventProperties?: Record<string, unknown>): Promise<void> {
    try {
        const amplitude = await loadAmplitude();
        if (!amplitude) {
            return;
        }

        amplitude.track(eventName, eventProperties);
    } catch {
        // analytics is best-effort; never let it disrupt the calling flow
    }
}
